// src/modules/units/tyres/views/yards.api.js
//
// The yard resource client for the tyre area.
//
// Yards belong to a CUSTOMER, not to a tyre, so this is a sibling of tyres.api.js
// rather than part of it: /api/customer/{customerId}/yard. It follows
// tyres.api.js's request() envelope on purpose — every caller in this module
// already speaks { success, status, data, message }, so a yard read and a tyre
// read handle the same way. (The older customers.api.js throws instead; matching
// the module we are extending, not the app-wide average, is the point.)
//
// FIELD-NAME CAUTION. This endpoint names its id `Id` (capital I) and its label
// `name`, and also returns `customerName` / `customerHierarchyOrder`. GET
// /api/tyre, by contrast, uses `yardId` / `yardName`. Yard objects keep their
// native shape here; the `Id -> yardId` translation happens wherever a yard
// scopes a tyre query, never by mutating the yard.
//
// This does not emit CHANGED — changing a yard is not a tyre write. It emits
// YARDS_CHANGED on successful writes, so the header selector and the Yards view
// rebuild their lists without either one polling. Scope SELECTION is separate
// again: that is announced by tyre-scope.js as SCOPE_CHANGED.

import { getUserData } from '../../../auth/views/login/auth.api.js';
import { emitTyreEvent, TYRE_EVENTS } from '../shared/tyre-events.js';

const baseUrl = 'https://dashboard.pressuretrack.co.za/api';

// The customer whose yards we manage. parentCustomerId is the signed-in customer.
//
// NOTE (service agents): a service agent acting on another account scopes the
// rest of the app through global.customerId (see customers.api.js getUsers). This
// module is being kept off that global, and GET /api/tyre already scopes to the
// active account server-side via the token, so yards should follow the same
// account. If agent "acting-as" needs to reach yards, the clean fix is to expose
// the active customerId through getUserData() rather than reading the global
// here — flagged for the team, not papered over.
function currentCustomerId() {
  return getUserData()?.parentCustomerId ?? null;
}

export function canManageYards() {
  return getUserData()?.userRole === 'Administrator';
}

async function request(path, options = {}) {
  const { token } = getUserData() || {};

  try {
    const response = await fetch(`${baseUrl}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(options.headers || {})
      },
      credentials: 'include'
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        success: false,
        status: response.status,
        message: data?.message || data?.error || 'Yard request failed.',
        data
      };
    }

    return { success: true, status: response.status, data };
  } catch (error) {
    return { success: false, status: 0, message: error?.message || 'Yard request failed.', data: null };
  }
}

// Explicit, positive page size. NEVER limit=0.
//
// `limit=0` as "give me everything" is a convention this backend uses elsewhere,
// but it was never verified against THIS endpoint, whose schema documents limit
// as an integer defaulting to 20. Sending an unverified value to a live endpoint
// is how the yard list went empty: a rejected request returns no rows, and every
// consumer here treats "no rows" as "no yards" rather than as a failure. Explicit
// positive limits cannot be rejected on those grounds.
const YARD_PAGE_SIZE = 100;
const YARD_MAX_PAGES = 20;

function yardQuery({ inclChild = true, customerId, sort, limit, offset = 0 }) {
  const q = new URLSearchParams();
  q.set('inclChild', String(inclChild));
  q.set('limit', String(limit));
  q.set('offset', String(offset));
  if (customerId) q.set('customerId', customerId);
  // Only ever sent when a caller explicitly asks. The endpoint documents its own
  // default as `Id`; whether it accepts `name` is unverified, so ordering is done
  // below on the client instead, where it cannot fail.
  if (sort) q.set('sort', sort);
  return q;
}

/**
 * List the current customer's yards -- ALL of them.
 *
 * inclChild defaults to true: an admin may own child-customer yards, and the
 * header selector and the create/edit form all want the full hierarchy so they
 * never diverge.
 *
 * Pages with explicit positive limits until the server returns a short page. That
 * fixes the original defect (no limit was sent at all, so the endpoint applied its
 * default of 20 and silently truncated the list) without betting on limit=0.
 *
 * A failed request is RETURNED AS A FAILURE, never as an empty list. Callers paint
 * whatever they get, so a swallowed error and an empty yard list are
 * indistinguishable on screen -- which is exactly the failure mode that leaves a
 * tyre with no way to be assigned a yard.
 *
 * Results are sorted by name on the client. The endpoint's default order is `Id`,
 * which put the dropdowns in effectively arbitrary order.
 */
export async function fetchYards(opts = {}) {
  const cid = currentCustomerId();
  if (!cid) {
    return { success: false, status: 0, message: 'No customer in session.', data: null };
  }

  const path = `/customer/${encodeURIComponent(cid)}/yard`;

  // A caller that names a page gets exactly that page, untouched.
  if (Number(opts.limit) > 0) {
    return request(`${path}?${yardQuery(opts).toString()}`, { method: 'GET' });
  }

  const rows = [];
  let count = NaN;
  let status = 200;

  for (let page = 0; page < YARD_MAX_PAGES; page++) {
    const res = await request(
      `${path}?${yardQuery({ ...opts, limit: YARD_PAGE_SIZE, offset: rows.length }).toString()}`,
      { method: 'GET' }
    );
    if (!res.success) return res;                 // surface it; never fake an empty list
    status = res.status;
    const batch = Array.isArray(res.data?.results) ? res.data.results : [];
    count = Number(res.data?.count);
    rows.push(...batch);
    if (batch.length < YARD_PAGE_SIZE) break;     // short page = last page
    if (Number.isFinite(count) && rows.length >= count) break;
  }

  rows.sort((a, b) => String(a?.name ?? '').localeCompare(String(b?.name ?? ''), undefined, { numeric: true }));

  return {
    success: true,
    status,
    data: { count: Number.isFinite(count) ? count : rows.length, results: rows }
  };
}

/**
 * Create a yard for the current customer.
 *
 * Body is { name, IMEI }. `name` is required; `IMEI` is sent only when given — a
 * yard may be created before its gateway is provisioned. Confirm against the
 * backend whether IMEI is truly optional; until then, omitting it is the safe
 * default rather than sending an empty string.
 */
export async function createYard({ name, IMEI } = {}) {
  const cid = currentCustomerId();
  if (!cid) {
    return { success: false, status: 0, message: 'No customer in session.', data: null };
  }

  const body = { name };
  if (IMEI) body.IMEI = IMEI;

  const res = await request(`/customer/${encodeURIComponent(cid)}/yard`, {
    method: 'POST',
    body: JSON.stringify(body)
  });

  // Announce the new yard so the header selector and the Yards view rebuild.
  if (res.success) emitTyreEvent(TYRE_EVENTS.YARDS_CHANGED, { reason: 'create' });

  return res;
}

// inclChild=true may return yards belonging to a child customer. Mutations must
// use that yard's owning customerId, not the signed-in parent's id.
function yardPath(yard) {
  const id = yard?.Id;
  const customerId = yard?.customerId || currentCustomerId();
  return id && customerId
    ? `/customer/${encodeURIComponent(customerId)}/yard/${encodeURIComponent(id)}`
    : null;
}

function mutationError(message, status = 0) {
  return { success: false, status, message, data: null };
}

export async function updateYard(yard, { name, IMEI } = {}) {
  if (!canManageYards()) return mutationError('Administrator access is required to edit yards.', 403);
  const path = yardPath(yard);
  if (!path) return mutationError('The yard or its customer is missing.');

  // Unlike create, a blank IMEI must be sent so an existing value can be cleared.
  const res = await request(path, {
    method: 'PATCH',
    body: JSON.stringify({ name: String(name || '').trim(), IMEI: String(IMEI ?? null).trim() })
  });
  if (res.success) emitTyreEvent(TYRE_EVENTS.YARDS_CHANGED, { reason: 'update', yardId: yard.Id });
  return res;
}

export async function deleteYard(yard) {
  if (!canManageYards()) return mutationError('Administrator access is required to delete yards.', 403);
  const path = yardPath(yard);
  if (!path) return mutationError('The yard or its customer is missing.');

  const res = await request(path, { method: 'DELETE' });
  if (res.success) emitTyreEvent(TYRE_EVENTS.YARDS_CHANGED, { reason: 'delete', yardId: yard.Id });
  return res;
}
